import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';

interface AuthContextType {
  currentUser: User | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const login = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ Google login successful:', result.user.email);
    } catch (error: any) {
      console.error('❌ Login error:', error);
      
      // Daha detaylı hata mesajları
      if (error.code === 'auth/api-key-not-valid') {
        throw new Error('Firebase API key geçersiz. Lütfen .env dosyasını kontrol edin.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Giriş penceresi kapatıldı. Lütfen tekrar deneyin.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Pop-up engellendi. Lütfen tarayıcı ayarlarını kontrol edin.');
      } else {
        throw new Error('Giriş yapılırken bir hata oluştu: ' + error.message);
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      console.log('✅ Logout successful');
    } catch (error: any) {
      console.error('❌ Logout error:', error);
      throw new Error('Çıkış yapılırken bir hata oluştu: ' + error.message);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth, 
      (user) => {
        setCurrentUser(user);
        setLoading(false);
        if (user) {
          console.log('👤 User state changed: Logged in as', user.email);
        } else {
          console.log('👤 User state changed: Logged out');
        }
      },
      (error) => {
        console.error('❌ Auth state change error:', error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 